import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '~/lib/api-client';
import { backgroundChatKeys } from '~/lib/query-keys';

/**
 * FRONT-765: the composer's Stop button now calls this for every turn,
 * including ones that finished (or were never tracked) before the user
 * pressed it — the server 404s ("no running run to cancel"), which is a
 * normal race, not a failure worth an error toast. Extracted as a pure
 * function so the decision is unit-testable without mounting `useMutation`.
 */
export function shouldToastCancelError(err: unknown): boolean {
  return !(err instanceof ApiError && err.status === 404);
}

/**
 * Stop a running background inbox-chat — or clear a stuck/orphaned "running"
 * card left behind when a redeploy killed the handler mid-turn. Marks the row
 * `cancelled` server-side; a live handler stops at its next round boundary, and
 * the card flips to a neutral "Stopped" state once the invalidation lands.
 */
export function useCancelBackgroundChat() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ runId }: { runId: string }) =>
      api.post(`/api/background-chats/${encodeURIComponent(runId)}/cancel`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: backgroundChatKeys.all });
    },
    onError: (err) => {
      if (shouldToastCancelError(err)) {
        toast.error(`Stop failed: ${(err as Error).message}`);
      }
    },
  });
}
